import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  Renderer2,
} from '@angular/core';
import { ColorTheme, ThemeService } from '@marineform/fe-util';

@Directive({
  selector: '[active]',
  standalone: true,
})
export class ActiveDirective implements OnInit {
  /** When false, only the overlay press effect runs (no scale transform). */
  @Input() activeScale = true;

  private overlay: HTMLElement | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private themeService: ThemeService,
  ) {
    this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    this.renderer.setStyle(this.el.nativeElement, 'overflow', 'hidden');
  }

  ngOnInit() {
    if (this.activeScale) {
      this.renderer.setStyle(
        this.el.nativeElement,
        'transition',
        'transform 300ms ease',
      );
    }
  }

  // Add dark overlay
  private addOverlay() {
    if (this.overlay) return; // already exists
    this.overlay = this.renderer.createElement('span');
    this.renderer.addClass(this.overlay, 'active-overlay');

    this.renderer.setStyle(this.overlay, 'position', 'absolute');
    this.renderer.setStyle(this.overlay, 'top', '0');
    this.renderer.setStyle(this.overlay, 'left', '0');
    this.renderer.setStyle(this.overlay, 'right', '0');
    this.renderer.setStyle(this.overlay, 'bottom', '0');
    if (this.themeService.themeValue() === ColorTheme.DARK) {
      // white overlay for dark mode
      this.renderer.setStyle(
        this.overlay,
        'background',
        'rgba(255, 255, 255, 0.1)',
      );
    } else {
      // black overlay for light mode
      this.renderer.setStyle(this.overlay, 'background', 'rgba(0, 0, 0, 0.05)');
    }
    this.renderer.setStyle(this.overlay, 'opacity', '0');
    this.renderer.setStyle(this.overlay, 'transition', 'opacity 150ms ease');
    this.renderer.setStyle(this.overlay, 'pointer-events', 'none');
    this.renderer.setStyle(this.overlay, 'border-radius', 'inherit');

    this.renderer.appendChild(this.el.nativeElement, this.overlay);

    // force reflow so transition applies
    void this.overlay?.offsetWidth;

    this.renderer.setStyle(this.overlay, 'opacity', '1');
  }

  // Remove dark overlay
  private removeOverlay() {
    if (!this.overlay) return;
    this.renderer.setStyle(this.overlay, 'opacity', '0');

    setTimeout(() => {
      if (this.overlay) {
        this.renderer.removeChild(this.el.nativeElement, this.overlay);
        this.overlay = null;
      }
    }, 150);
  }

  // --- Mouse events ---
  @HostListener('mousedown')
  onMouseDown() {
    this.scaleDown();
    this.addOverlay();
  }

  @HostListener('mouseup')
  @HostListener('mouseleave')
  onMouseUp() {
    this.scaleUp();
    this.removeOverlay();
  }

  // --- Touch events ---
  @HostListener('touchstart')
  onTouchStart() {
    this.scaleDown();
    this.addOverlay();
  }

  @HostListener('touchend')
  @HostListener('touchcancel')
  onTouchEnd() {
    this.scaleUp();
    this.removeOverlay();
  }

  private scaleDown() {
    if (!this.activeScale) return;
    this.renderer.setStyle(this.el.nativeElement, 'transform', 'scale(0.96)');
  }

  private scaleUp() {
    if (!this.activeScale) return;
    this.renderer.setStyle(this.el.nativeElement, 'transform', 'scale(1)');
  }
}
